import classnames from 'classnames';
import { css } from 'emotion';
import React from 'react';
import { Link as ReactRouterDOMLink } from 'react-router-dom';
import Hoverable from '../Hoverable';
import If from '../If';
import Pressable from '../Pressable';
import styles from './Link.sass';

const cx = classnames.bind(styles);

const noop = () => null;

type ColorProp = 'purple' | 'gray';
interface LinkProps {
  children: React.ReactNode;
  isInline: boolean;
  to: string;
  onClick: () => void;
  color: ColorProp;
  target: '_self' | '_blank';
  isIncludingSpacesAround: boolean;
}
interface LinkableProps {
  className: string;
  style: any;
  children: (args: {
    isHovering: boolean;
    isPressed: boolean;
  }) => React.ReactNode;
  isInline: boolean;
  to: string;
  onClick: () => void;
  color: 'purple' | 'gray';
  target: '_self' | '_blank';
  isFullWidth: boolean;
  dataCy?: string;
}

const linkTextDefaultProps = {
  isInline: false,
  onClick: noop,
  target: null,
  color: 'purple',
  isIncludingSpacesAround: false,
};
const linkableDefaultProps = {
  isInline: false,
  onClick: noop,
  target: null,
  color: 'purple',
  isIncludingSpacesAround: false,
  className: '',
  style: {},
  isFullWidth: false,
};
const addSpacesAround = (element: React.ReactNode) => (
  <React.Fragment>
    {'\u00A0'}
    {element}
    {'\u00A0'}
  </React.Fragment>
);

const isUrlExternal = (url: string) => {
  // https://stackoverflow.com/questions/10687099/how-to-test-if-a-url-string-is-absolute-or-relative
  const regexForProtocolInUrlString = new RegExp('^(?:[a-z]+:)?//', 'i');
  const isProtocolIncludedInString = regexForProtocolInUrlString.test(url);
  return isProtocolIncludedInString;
};

interface Params {
  eventCategory: string;
  eventAction: string;
  eventLabel: string;
  transport?: string;
  hitCallback?: () => void;
}
const Linkable = ({
  children,
  to,
  onClick,
  target,
  className,
  style,
  isFullWidth,
  dataCy,
}: LinkableProps) => {
  const linkableClassName = css({
    '&, &:hover, &:visited, &:link, &:active': {
      textDecoration: 'none',
    },
    // color: 'inherit',
    display: 'inline-block',
    ...(isFullWidth ? { width: '100%' } : {}),
  });

  return (
    <If
      condition={isUrlExternal(to)}
      ifRender={() => (
        <Pressable
          display="inline"
          isDefaultBehaviorAllowed={true}
          fullWidth={isFullWidth}
          onPress={() => {
            const params = {
              eventCategory: 'Outbound',
              eventAction: 'Click',
              eventLabel: to,
              ...(target === '_blank'
                ? {
                    transport: 'beacon',
                    hitCallback: () => {
                      document.location;
                    },
                  }
                : {}),
            };
            // TODO: Shouldn't use ga global
            // @ts-ignore
            if (window.ga !== undefined) {
              // @ts-ignore
              window.ga('send', 'event', params);
            }
            onClick();
          }}
        >
          {({ isPressed }) => (
            <Hoverable fullWidth={isFullWidth} display="inline">
              {({ isHovering }) => (
                <a
                  className={`${linkableClassName} ${className}`}
                  style={{
                    // ...baseStyle,
                    ...style,
                    // ...(isFullWidth ? { width: '100%' } : {}),
                  }}
                  rel={target === '_blank' ? 'noopener noreferrer' : ''}
                  data-vars-outbound-link={to}
                  href={to}
                  target={target}
                >
                  {children({ isPressed, isHovering })}
                </a>
              )}
            </Hoverable>
          )}
        </Pressable>
      )}
      elseRender={() => (
        <Pressable
          isDefaultBehaviorAllowed={true}
          fullWidth={isFullWidth}
          onPress={() => {
            onClick();
          }}
        >
          {({ isPressed }) => (
            <Hoverable fullWidth={isFullWidth}>
              {({ isHovering }) => (
                <ReactRouterDOMLink
                  data-cy={dataCy}
                  to={to}
                  className={`${linkableClassName} ${className}`}
                  style={{
                    // ...baseStyle,
                    ...style,
                    // ...(isFullWidth ? { width: '100%' } : {}),
                  }}
                  target={target ? target : undefined}
                >
                  {children({ isPressed, isHovering })}
                </ReactRouterDOMLink>
              )}
            </Hoverable>
          )}
        </Pressable>
      )}
    />
  );
};

const LinkText = ({
  isInline,
  color,
  isIncludingSpacesAround,
  children,
  to,
  onClick,
  target,
}: LinkProps) => {
  const linkableClassName = css({
    '&, &:hover, &:visited, &:link, &:active': {
      textDecoration: 'none',
    },
  });
  const linkStyles = {
    display: isInline ? 'inline' : 'block',
  };

  const getColorClassName = (color: ColorProp) => {
    switch (color) {
      case 'gray':
        return css({
          color: '#7f8287',
          '&:hover': {
            color: '#292F36',
          },
        });
      case 'purple':
        return css({
          color: '#8940FA',
          '&:hover': {
            color: '#6732B9',
          },
        });
      default:
        const exhaustiveCheck: never = color;
        return exhaustiveCheck;
    }
  };
  return (
    <Linkable
      className={`${linkableClassName} ${getColorClassName(color)}`}
      style={{
        cursor: 'pointer',
        ...linkStyles,
      }}
      to={to}
      onClick={onClick}
      target={target}
    >
      {({ isPressed, isHovering }) =>
        isIncludingSpacesAround ? addSpacesAround(children) : children
      }
    </Linkable>
  );
};

Linkable.defaultProps = linkableDefaultProps;
LinkText.defaultProps = linkTextDefaultProps;

export { LinkText, Linkable };
